import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  AangifteBerekening,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from "@snelstart-ventures/fiscaal/domain-logic";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  shareReplay,
  switchMap,
  take
} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {HeffingskortingenBerekeningenComponent} from "./overzicht-heffingskortingen/heffingskortingen.component";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiSearchControlModule} from "@snelstart/ui-search-control";
import {SpecBelastingBox1Component} from "./overzicht-belasting/spec-belasting-box1.component";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-ib-overzicht',
    templateUrl: './ib-overzicht.component.html',
    styleUrls: ['./ib-overzicht.component.scss'],
    imports: [
        HeffingskortingenBerekeningenComponent,
        ReactiveFormsModule,
        SharedUiFormControlsModule,
        SharedUiSearchControlModule,
        SpecBelastingBox1Component,
    ],
})
export class IbOverzichtComponent implements OnInit {
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(IbRekenService);
    public dataVisible$ = new BehaviorSubject<boolean>(false);
    public isPartner = false;
    public isPartnerView = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        box1: new FormGroup({
            inkomen: new FormControl<number | null>(0),
            persoonsgebondenAftrek: new FormControl<number | null>(0),
            belastbaarInkomen: new FormControl<number | null>(0),
            inkomstenbelasting: new FormControl<number | null>(0),
            conserverendeAanslag: new FormControl<number | null>(0),
        }),
        box2: new FormGroup({
            inkomen: new FormControl<number | null>(0),
            persoonsgebondenAftrek: new FormControl<number | null>(0),
            belastbaarInkomen: new FormControl<number | null>(0),
            inkomstenbelasting: new FormControl<number | null>(0),
            conserverendeAanslag: new FormControl<number | null>(0),
        }),
        box3: new FormGroup({
            inkomen: new FormControl<number | null>(0),
            persoonsgebondenAftrek: new FormControl<number | null>(0),
            belastbaarInkomen: new FormControl<number | null>(0),
            inkomstenbelasting: new FormControl<number | null>(0),
        }),
        opbouwPGA: new FormGroup({
            restantPgaVoorgaandeJaren: new FormControl<number | null>(0),
            onderhoudsverplichtingen: new FormControl<number | null>(0),
            specifiekeZorgkosten: new FormControl<number | null>(0),
            weekenduitgaven: new FormControl<number | null>(0),
            scholingsuitgaven: new FormControl<number | null>(0),
            monumentenpanden: new FormControl<number | null>(0),
            aftrekbareGiften: new FormControl<number | null>(0),
            kwijtscheldingDurfkapitaal: new FormControl<number | null>(0),
            aandeelPartnerPga: new FormControl<number | null>(0),
        }),
        heffingskortingen: new FormGroup({
            totaal: new FormControl<number | null>(0),
            opbouw: new FormGroup({
                algemeneHeffingskorting: new FormControl<number | null>(0),
                arbeidskorting: new FormControl<number | null>(0),
                inkomensafhankelijkeCombinatieKorting: new FormControl<number | null>(0),
                ouderenkorting: new FormControl<number | null>(0),
                alleenstaandeOuderenkorting: new FormControl<number | null>(0),
                wajong: new FormControl<number | null>(0),
                grondslagGroeneBeleggingen: new FormControl<number | null>(0),
                groeneBeleggingen: new FormControl<number | null>(0),
                levensloopverlof: new FormControl<number | null>(0),
                werkbonus: new FormControl<number | null>(0),
                vutkorting: new FormControl<number | null>(0),
                maximering: new FormControl<number | null>(0),
                verhogingPartner: new FormControl<number | null>(0),
            }),
        }),
        premieVolksverzekeringen: new FormControl<number | null>(0),
        gecombineerdeInkomensheffing: new FormControl<number | null>(0),
        verrekendeBelastingKortingBox2: new FormControl<number | null>(0),
        alBetaaldeLoonbelasting: new FormControl<number | null>(0),
        alBetaaldeDividendbelasting: new FormControl<number | null>(0),
        voorlopigeAanslag: new FormControl<number | null>(0),
        voorlopigeTeruggaaf: new FormControl<number | null>(0),
        aanslaggrens: new FormControl<number | null>(0),
        verschuldigdeInkomstenbelasting: new FormControl<number | null>(0),
        verschuldigdeZVW: new FormControl<number | null>(0),
        nietToegerekendePga: new FormControl<number | null>(0),
        verzamelinkomen: new FormControl<number | null>(0),
    });

    ngOnInit(): void {
        const berekening$ = this.incomeTaxEntity$.pipe(
            take(1),
            filter((data): data is IncomeTaxEntity => !!data.aangifte),
            switchMap((data) => {
                this.isPartner = data.aangifte?.gezamenlijkeAangifte ?? false;
                return this.bokkzClient.getIncomeTaxCalculation(data.aangifte!);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            takeUntilDestroyed(this.destroyRef),
        );

        combineLatest([berekening$, this.dataService.partnerAangifte$.pipe(distinctUntilChanged())])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([berekening, isPartner]) => {
                this.isPartnerView = isPartner; // ← toevoegen
                this.dataVisible$.next(false);
                this.resetAndPatchForms(isPartner ? (berekening.partnerBerekening ?? berekening) : berekening);
            });
    }

    private resetAndPatchForms(details: AangifteBerekening): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.patchValue(details, { emitEvent: false });
        this.formGroup.disable({ emitEvent: false });
        this.dataVisible$.next(true);
    }
}
